import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaSave, FaUndo, FaPlus, FaTimes } from 'react-icons/fa';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { toast } from 'react-toastify';

const AdminAbout = () => {
  const [about, setAbout] = useState({
    title: '',
    subtitle: '',
    mission: '',
    vision: '',
    story: '',
    philosophy: '',
    core_values: [],
    timeline: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getAbout();
      console.log('About response:', response.data);
      
      if (response.data) {
        // Ensure core_values is an array
        let coreValues = response.data.core_values || [];
        if (typeof coreValues === 'string') {
          try {
            coreValues = JSON.parse(coreValues) || [];
          } catch {
            coreValues = [];
          }
        }
        if (!Array.isArray(coreValues)) {
          coreValues = [];
        }
        
        // Ensure timeline is an array
        let timeline = response.data.timeline || [];
        if (typeof timeline === 'string') {
          try {
            timeline = JSON.parse(timeline) || [];
          } catch {
            timeline = [];
          }
        }
        if (!Array.isArray(timeline)) {
          timeline = [];
        }
        
        setAbout({
          title: response.data.title || '',
          subtitle: response.data.subtitle || '',
          mission: response.data.mission || '',
          vision: response.data.vision || '',
          story: response.data.story || '',
          philosophy: response.data.philosophy || '',
          core_values: coreValues,
          timeline: timeline
        });
      }
    } catch (error) {
      console.error('Error fetching about data:', error);
      setError('Failed to load about data. Please try again.');
      toast.error('Failed to fetch about data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Ensure core_values is an array
      const submitData = {
        ...about,
        core_values: Array.isArray(about.core_values) ? about.core_values : [],
        timeline: Array.isArray(about.timeline) ? about.timeline : []
      };
      
      await apiService.updateAbout(submitData);
      toast.success('About section updated successfully');
      fetchAbout();
    } catch (error) {
      toast.error('Failed to update about section');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset changes?')) {
      fetchAbout();
    }
  };

  const handleArrayChange = (field, value) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    setAbout({ ...about, [field]: items });
  };

  // Add a new timeline item
  const addTimelineItem = () => {
    const newItem = {
      year: '',
      title: '',
      description: '',
      icon: 'code'
    };
    setAbout({
      ...about,
      timeline: [...(about.timeline || []), newItem]
    });
  };

  // Remove a timeline item
  const removeTimelineItem = (index) => {
    const newTimeline = [...(about.timeline || [])];
    newTimeline.splice(index, 1);
    setAbout({ ...about, timeline: newTimeline });
  };

  // Update a timeline item
  const updateTimelineItem = (index, field, value) => {
    const newTimeline = [...(about.timeline || [])];
    newTimeline[index] = { ...newTimeline[index], [field]: value };
    setAbout({ ...about, timeline: newTimeline });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <p className="text-red-400 text-lg">{error}</p>
          <button 
            onClick={fetchAbout}
            className="mt-4 px-4 py-2 bg-primary-500 rounded-lg text-white hover:bg-primary-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">About Section</h1>
          <p className="text-gray-400 mt-1">Manage your about page content</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Title
            </label>
            <input
              type="text"
              value={about.title || ''}
              onChange={(e) => setAbout({ ...about, title: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Your name or title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Subtitle
            </label>
            <input
              type="text"
              value={about.subtitle || ''}
              onChange={(e) => setAbout({ ...about, subtitle: e.target.value })}
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Your professional title"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Mission
          </label>
          <textarea
            rows="2"
            value={about.mission || ''}
            onChange={(e) => setAbout({ ...about, mission: e.target.value })}
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Your mission statement"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Vision
          </label>
          <textarea
            rows="2"
            value={about.vision || ''}
            onChange={(e) => setAbout({ ...about, vision: e.target.value })}
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Your vision statement"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Story
          </label>
          <textarea
            rows="4"
            value={about.story || ''}
            onChange={(e) => setAbout({ ...about, story: e.target.value })}
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Your personal story..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Development Philosophy
          </label>
          <textarea
            rows="3"
            value={about.philosophy || ''}
            onChange={(e) => setAbout({ ...about, philosophy: e.target.value })}
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Your development philosophy..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Core Values (comma separated)
          </label>
          <input
            type="text"
            value={Array.isArray(about.core_values) ? about.core_values.join(', ') : ''}
            onChange={(e) => handleArrayChange('core_values', e.target.value)}
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Innovation, Excellence, Integrity, Growth"
          />
          {Array.isArray(about.core_values) && about.core_values.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {about.core_values.map((value, i) => (
                <span key={i} className="px-2 py-1 text-sm rounded bg-primary-500/10 border border-primary-500/20 text-primary-400">
                  {value}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Timeline Section */}
        <div className="border-t border-white/10 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Timeline</h3>
            <button
              type="button"
              onClick={addTimelineItem}
              className="px-3 py-1 text-sm bg-primary-500/20 hover:bg-primary-500/30 text-primary-400 rounded-lg flex items-center gap-1"
            >
              <FaPlus size={12} />
              Add Item
            </button>
          </div>
          
          {Array.isArray(about.timeline) && about.timeline.map((item, index) => (
            <div key={index} className="glass rounded-lg p-4 mb-3 relative">
              <button
                type="button"
                onClick={() => removeTimelineItem(index)}
                className="absolute top-2 right-2 p-1 rounded-lg hover:bg-red-500/20 text-gray-400 hover:text-red-400"
              >
                <FaTimes size={14} />
              </button>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Year</label>
                  <input
                    type="text"
                    value={item.year || ''}
                    onChange={(e) => updateTimelineItem(index, 'year', e.target.value)}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="2023 - Present"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Title</label>
                  <input
                    type="text"
                    value={item.title || ''}
                    onChange={(e) => updateTimelineItem(index, 'title', e.target.value)}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="B.Sc. Software Engineering"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">Description</label>
                  <input
                    type="text"
                    value={item.description || ''}
                    onChange={(e) => updateTimelineItem(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Federal University Dutse, Nigeria"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-4 pt-4 border-t border-white/10">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg font-medium text-white hover:shadow-lg hover:shadow-primary-500/30 transition-all duration-300 flex items-center gap-2 disabled:opacity-50"
          >
            <FaSave />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="px-6 py-2 border border-white/10 rounded-lg font-medium text-gray-400 hover:bg-white/5 transition-all duration-300 flex items-center gap-2"
          >
            <FaUndo />
            Reset
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminAbout;